import { useMemo } from "react";
import { useSelector } from "react-redux";
import { PackageType } from "@b2bportal/core-packages";
import { ProductType } from "@b2bportal/core-types";
import {
  ParentState,
  TravelerSelectors,
  useCheckoutStateSelector,
} from "@checkout/index";
import { useI18nContext, useI18nList } from "@hopper-b2b/i18n";
import {
  getExpectedTravelerComposition,
  getSelectedTravelerComposition,
  getSuggestedInfantSeatType,
  type InfantSeatType,
  isSameTravelerComposition,
} from "./packageTravelerComposition";
import { PackageSearchSelectors } from "@/store/packages/selectors";

/**
 * Searched vs. selected traveler breakdown for package checkout.
 */
const usePackageTravelerCompositions = () => {
  const seatedTravelers = useCheckoutStateSelector(
    TravelerSelectors.getAllSelectedTravelers
  );
  const lapInfantCount = useCheckoutStateSelector(
    (state) =>
      state.context[ParentState.travelerInformation]?.selectedLapInfantIds
        ?.length ?? 0
  );
  const guests = useSelector(PackageSearchSelectors.getSearchFormGuests);
  const dateRange = useSelector(PackageSearchSelectors.getSearchFormDateRange);

  const expectedComposition = useMemo(
    () => getExpectedTravelerComposition(guests),
    [guests]
  );
  const selectedComposition = useMemo(
    () =>
      getSelectedTravelerComposition({
        seatedTravelers,
        lapInfantCount,
        tripDate: dateRange.from,
      }),
    [seatedTravelers, lapInfantCount, dateRange.from]
  );

  return {
    expectedComposition,
    selectedComposition,
    selectedTravelerCount: seatedTravelers.length + lapInfantCount,
  };
};

/**
 * Pre-selects lap vs. own seat in the infant seat picker based on which
 * searched infant slots are still unfilled.
 */
export const usePackageInfantSeatSuggestion = (): InfantSeatType => {
  const { expectedComposition, selectedComposition } =
    usePackageTravelerCompositions();
  return (
    getSuggestedInfantSeatType(expectedComposition, selectedComposition) ??
    "OnLap"
  );
};

export const usePackageTravelerCountValidation = (
  productType?: ProductType
) => {
  const { t } = useI18nContext();
  const { formatList } = useI18nList();

  const { expectedComposition, selectedComposition, selectedTravelerCount } =
    usePackageTravelerCompositions();

  const expectedPaxCount = useSelector(
    PackageSearchSelectors.getSearchFormPassengerCount
  );
  const packageType = useSelector(
    PackageSearchSelectors.getSearchFormPackageType
  );
  // Flight packages are priced per passenger type (ADT/CNN/INF/INS), so the
  // selected travelers must match the searched breakdown, not just the total.
  const packageTypeIncludesFlights = packageType !== PackageType.HOTEL_CAR;

  const hasCorrectTravelerCount =
    productType !== ProductType.PACKAGE ||
    (packageTypeIncludesFlights
      ? isSameTravelerComposition(selectedComposition, expectedComposition)
      : selectedTravelerCount === expectedPaxCount);

  const showTravelerCountError =
    !hasCorrectTravelerCount && selectedTravelerCount > 0;

  const travelerCountErrorMessage = useMemo(() => {
    if (!packageTypeIncludesFlights) {
      return t("checkout.travelerInformation.selectTravelersCount", {
        count: expectedPaxCount,
      });
    }
    // Build "2 adults, 1 child and 1 infant on lap" from only the
    // categories present in the search.
    const { adults, children, infantsOnLap, infantsInSeat } =
      expectedComposition;
    const breakdown = [
      t("checkout.travelerInformation.travelerBreakdown.adult", {
        count: adults,
      }),
    ];
    if (children) {
      breakdown.push(
        t("checkout.travelerInformation.travelerBreakdown.child", {
          count: children,
        })
      );
    }
    if (infantsOnLap) {
      breakdown.push(
        t("checkout.travelerInformation.travelerBreakdown.infantOnLap", {
          count: infantsOnLap,
        })
      );
    }
    if (infantsInSeat) {
      breakdown.push(
        t("checkout.travelerInformation.travelerBreakdown.infantInSeat", {
          count: infantsInSeat,
        })
      );
    }
    return t("checkout.travelerInformation.selectTravelersBreakdown", {
      travelers: formatList(breakdown, { type: "conjunction" }),
    });
  }, [
    t,
    formatList,
    packageTypeIncludesFlights,
    expectedPaxCount,
    expectedComposition,
  ]);

  return {
    selectedTravelerCount,
    expectedPaxCount,
    hasCorrectTravelerCount,
    showTravelerCountError,
    travelerCountErrorMessage,
  };
};
